// AAMVA PDF417 test-barcode catalog, ported verbatim from the retired Angular
// admin's barcode-testing page (barcode-testing.component.ts). Every sample's
// fields are precision test vectors for the on-device ID scanner/validator —
// do not "fix" or normalize any of the raw strings.

export interface AamvaTestCase {
  id: string;
  title: string;
  description: string;
  category: string;
  data: Record<string, string>;
  iin?: string; // State-specific IIN code
  expected: {
    type: 'valid' | 'invalid';
    status: string;
    notes: string;
  };
}

// IIN codes by jurisdiction.
export const IIN_CODES: Record<string, string> = {
  CA: '636014',
  NY: '636001',
  TX: '636015',
  FL: '636010',
  WA: '636045',
  HI: '636047',
  ON: '636012', // Ontario, Canada
  BC: '636028', // British Columbia, Canada
};

/**
 * Builds the AAMVA/PDF417 payload string for a test case (the `@\nANSI ...`
 * header followed by one `<field><value>` line per data element).
 */
export function buildAamvaData(testCase: AamvaTestCase): string {
  const iin = testCase.iin ? IIN_CODES[testCase.iin] || '636014' : '636014';
  let aamvaData = `@\nANSI ${iin}080002DL00410288ZC03190008DLDCAD\n`;
  for (const [key, value] of Object.entries(testCase.data)) {
    aamvaData += `${key}${value}\n`;
  }
  return aamvaData;
}

/**
 * Builds the full 65-sample AAMVA catalog across 14 categories. Several
 * samples embed dates relative to "today" (age boundaries, expiration
 * edges), so this is a function rather than a static constant — matching
 * the original component's `initializeTestCases()` behavior.
 */
export function buildTestCases(): AamvaTestCase[] {
  const today = new Date();

  // ==================== DATE HELPER FUNCTIONS ====================

  // Get today's date in MMDDCCYY format
  const getTodayMMDDCCYY = () => {
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const ccyy = String(today.getFullYear());
    return mm + dd + ccyy;
  };

  // Get date offset from today in MMDDCCYY format
  const getDateOffset = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const ccyy = String(date.getFullYear());
    return mm + dd + ccyy;
  };

  // Get date X years ago in YYYYMMDD format (Canadian)
  const getYearsAgoYYYYMMDD = (years: number) => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - years);
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return yyyy + mm + dd;
  };

  // Get exact birthday X years ago (for precise age testing)
  const getExactBirthday = (yearsAgo: number) => {
    const birthday = new Date();
    birthday.setFullYear(birthday.getFullYear() - yearsAgo);
    const mm = String(birthday.getMonth() + 1).padStart(2, '0');
    const dd = String(birthday.getDate()).padStart(2, '0');
    const ccyy = String(birthday.getFullYear());
    return mm + dd + ccyy;
  };

  // Get birthday that turns X years old tomorrow
  const getBirthdayTurnsTomorrow = (yearsAgo: number) => {
    const birthday = new Date();
    birthday.setFullYear(birthday.getFullYear() - yearsAgo);
    birthday.setDate(birthday.getDate() + 1);
    const mm = String(birthday.getMonth() + 1).padStart(2, '0');
    const dd = String(birthday.getDate()).padStart(2, '0');
    const ccyy = String(birthday.getFullYear());
    return mm + dd + ccyy;
  };

  // Get date offset in YYYYMMDD format (Canadian)
  const getDateOffsetYYYYMMDD = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return yyyy + mm + dd;
  };

  // Format MMDDCCYY for display
  const formatDate = (mmddccyy: string) => {
    const mm = mmddccyy.substring(0, 2);
    const dd = mmddccyy.substring(2, 4);
    const ccyy = mmddccyy.substring(4, 8);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${months[parseInt(mm) - 1]} ${dd}, ${ccyy}`;
  };

  // ==================== TEST CASES ====================

  return [
    // ================================================================
    // CATEGORY 1: BASELINE VALID IDs (Tests 1-5)
    // Purpose: Verify legitimate IDs score 100% with no warnings
    // ================================================================
    {
      id: 'test-01',
      title: 'Test 01: Standard California ID',
      description: 'Baseline US ID with all standard fields - should score 100%',
      category: 'Baseline Valid IDs',
      iin: 'CA',
      data: {
        DCS: 'SMITH',
        DAC: 'JOHN',
        DAD: 'MICHAEL',
        DBB: '05151990', // May 15, 1990
        DBA: getDateOffset(365 * 5), // Expires in 5 years
        DBD: getDateOffset(-365 * 2), // Issued 2 years ago
        DAG: '123 Main Street',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
        DAY: 'BRO',
        DAZ: 'BRO',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No warnings shown - perfect baseline California ID',
      },
    },
    {
      id: 'test-02',
      title: 'Test 02: Standard New York ID',
      description: 'Different state, all fields present',
      category: 'Baseline Valid IDs',
      iin: 'NY',
      data: {
        DCS: 'JOHNSON',
        DAC: 'SARAH',
        DAD: 'MARIE',
        DBB: '08221988',
        DBA: getDateOffset(365 * 4),
        DBD: getDateOffset(-365),
        DAG: '456 Broadway',
        DAI: 'New York',
        DAJ: 'NY',
        DAK: '10001',
        DAY: 'BLU',
        DAZ: 'BLN',
        DAU: '505',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No warnings shown - valid New York ID',
      },
    },
    {
      id: 'test-03',
      title: 'Test 03: Standard Texas ID',
      description: 'Another state variation with all fields',
      category: 'Baseline Valid IDs',
      iin: 'TX',
      data: {
        DCS: 'WILLIAMS',
        DAC: 'ROBERT',
        DAD: 'JAMES',
        DBB: '11031985',
        DBA: getDateOffset(365 * 6),
        DBD: getDateOffset(-365 * 3),
        DAG: '789 Lone Star Drive',
        DAI: 'Austin',
        DAJ: 'TX',
        DAK: '78701',
        DAY: 'GRN',
        DAZ: 'BLK',
        DAU: '602',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No warnings shown - valid Texas ID',
      },
    },
    {
      id: 'test-04',
      title: 'Test 04: Canadian Ontario ID (YYYYMMDD)',
      description: 'Tests YYYYMMDD date format used by Canadian provinces',
      category: 'Baseline Valid IDs',
      iin: 'ON',
      data: {
        DCS: 'TREMBLAY',
        DAC: 'PIERRE',
        DAD: 'JEAN',
        DBB: getYearsAgoYYYYMMDD(35), // YYYYMMDD format
        DBA: getDateOffsetYYYYMMDD(365 * 5),
        DBD: getDateOffsetYYYYMMDD(-365 * 2),
        DAG: '100 Queen Street',
        DAI: 'Toronto',
        DAJ: 'ON',
        DAK: 'M5H 2N2',
        DAY: 'BRO',
        DAZ: 'BRO',
        DAU: '508',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No warnings - Canadian ID with YYYYMMDD dates parses correctly',
      },
    },
    {
      id: 'test-05',
      title: 'Test 05: Minimal Valid ID',
      description: 'Only required fields (name, DOB, expiration) - no optional fields',
      category: 'Baseline Valid IDs',
      iin: 'CA',
      data: {
        DCS: 'MINIMAL',
        DAC: 'JANE',
        DBB: '06151992',
        DBA: getDateOffset(365 * 3),
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No warnings - minimal required fields are sufficient',
      },
    },

    // ================================================================
    // CATEGORY 2: CRITICAL VALIDATION FAILURES (Tests 6-9)
    // Purpose: Test checks that MUST flag (impossible situations)
    // ================================================================
    {
      id: 'test-06',
      title: 'Test 06: Future Birth Date',
      description: 'DOB 30 days in future - physically impossible',
      category: 'Critical Validation Failures',
      iin: 'CA',
      data: {
        DCS: 'FUTURE',
        DAC: 'TEST',
        DAD: 'PERSON',
        DBB: getDateOffset(30), // 30 days in future!
        DBA: getDateOffset(365 * 5),
        DAG: '123 Future Street',
        DAI: 'San Diego',
        DAJ: 'CA',
        DAK: '92101',
        DAY: 'BLU',
        DAU: '508',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: '75% - Review Recommended',
        notes: 'CRITICAL: "Birth date appears to be in the future"',
      },
    },
    {
      id: 'test-07',
      title: 'Test 07: Impossible Age (130 years)',
      description: 'DOB = Jan 1, 1895 - person would be 130+ years old',
      category: 'Critical Validation Failures',
      iin: 'CA',
      data: {
        DCS: 'ANCIENT',
        DAC: 'METHUSELAH',
        DAD: 'ELDER',
        DBB: '01011895', // Born 1895
        DBA: getDateOffset(365 * 5),
        DAG: '999 Eternity Road',
        DAI: 'Palm Springs',
        DAJ: 'CA',
        DAK: '92262',
        DAY: 'GRY',
        DAU: '504',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: '75% - Review Recommended',
        notes: 'CRITICAL: "Birth date suggests an unusually old age (over 120)"',
      },
    },
    {
      id: 'test-08',
      title: 'Test 08: Issue After Expiration',
      description: 'DBD (issue date) > DBA (expiration) - logically impossible',
      category: 'Critical Validation Failures',
      iin: 'CA',
      data: {
        DCS: 'PARADOX',
        DAC: 'TEMPORAL',
        DAD: 'ERROR',
        DBB: '01011990',
        DBA: '01012024', // Expired
        DBD: '01012025', // Issued AFTER expiration
        DAG: '789 Paradox Lane',
        DAI: 'San Francisco',
        DAJ: 'CA',
        DAK: '94102',
        DAY: 'GRN',
        DAU: '506',
        DBC: '2',
      },
      expected: {
        type: 'invalid',
        status: '75% - Review Recommended',
        notes: 'CRITICAL: "ID issue date is after expiration date"',
      },
    },
    {
      id: 'test-09',
      title: 'Test 09: Multiple Critical Failures',
      description: 'Future DOB + Issue after expiration - 50% confidence',
      category: 'Critical Validation Failures',
      iin: 'CA',
      data: {
        DCS: 'MULTIFAIL',
        DAC: 'DOUBLE',
        DAD: 'CRITICAL',
        DBB: getDateOffset(60), // Future DOB
        DBA: '01012024', // Expired
        DBD: '01012025', // Issue after exp
        DAG: '111 Fail Street',
        DAI: 'Nowhere',
        DAJ: 'CA',
        DAK: '90000',
        DAY: 'BLU',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: '50% - Possible Issue',
        notes: 'Multiple CRITICAL flags: future DOB + issue after expiration',
      },
    },

    // ================================================================
    // CATEGORY 3: WARNING-LEVEL ISSUES (Tests 10-12)
    // Purpose: Test that minor issues don't cause false positives
    // ================================================================
    {
      id: 'test-10',
      title: 'Test 10: Minimal Data Valid',
      description: 'Minimal fields but all required data present - should pass',
      category: 'Warning-Level Issues',
      iin: 'CA',
      data: {
        DCS: 'MINIMAL',
        DAC: 'DATA',
        DBB: '05151990',
        DBA: getDateOffset(365),
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Minimal but valid - should pass without warnings',
      },
    },
    {
      id: 'test-11',
      title: 'Test 11: Missing First Name Only',
      description: 'Has last name but no first name - should still verify',
      category: 'Warning-Level Issues',
      iin: 'CA',
      data: {
        DCS: 'LASTNAME',
        // Missing DAC (first name)
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Test Street',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Missing first name should not prevent verification',
      },
    },
    {
      id: 'test-12',
      title: 'Test 12: Missing Name Fields',
      description: 'No DCS, DAC, or DCT - tests name extraction failure',
      category: 'Warning-Level Issues',
      iin: 'CA',
      data: {
        // No DCS, DAC, or DCT
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Nameless Ave',
        DAI: 'Anytown',
        DAJ: 'CA',
        DAK: '90000',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED (no name displayed)',
        notes: 'Should still verify age/expiration even without name',
      },
    },

    // ================================================================
    // CATEGORY 4: DATE FORMAT PARSING (Tests 13-18)
    // Purpose: Test all 4 date parsers work correctly
    // ================================================================
    {
      id: 'test-13',
      title: 'Test 13: MMDDYYYY Format',
      description: 'Standard US format: 03151990 = March 15, 1990',
      category: 'Date Format Parsing',
      iin: 'CA',
      data: {
        DCS: 'USFORMAT',
        DAC: 'STANDARD',
        DBB: '03151990', // March 15, 1990
        DBA: '03152030',
        DAG: '100 Standard Street',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Mar 15, 1990 - MMDDYYYY parses correctly',
      },
    },
    {
      id: 'test-14',
      title: 'Test 14: YYYYMMDD Format (Canadian)',
      description: 'Canadian format: 19900315 = March 15, 1990',
      category: 'Date Format Parsing',
      iin: 'ON',
      data: {
        DCS: 'CANADIAN',
        DAC: 'FORMAT',
        DBB: '19900315', // March 15, 1990 in YYYYMMDD
        DBA: '20300315',
        DAG: '100 Maple Street',
        DAI: 'Toronto',
        DAJ: 'ON',
        DAK: 'M5H 2N2',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Mar 15, 1990 - YYYYMMDD parses correctly',
      },
    },
    {
      id: 'test-15',
      title: 'Test 15: DDMMYYYY Format (European)',
      description: 'European format: 15031990 = March 15, 1990',
      category: 'Date Format Parsing',
      iin: 'CA',
      data: {
        DCS: 'EUROPEAN',
        DAC: 'FORMAT',
        DBB: '15031990', // March 15, 1990 in DDMMYYYY
        DBA: '15032030',
        DAG: '100 Euro Street',
        DAI: 'San Francisco',
        DAJ: 'CA',
        DAK: '94102',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Mar 15, 1990 - DDMMYYYY parses correctly (European format)',
      },
    },
    {
      id: 'test-16',
      title: 'Test 16: Dates with Dashes',
      description: 'Date with dashes: 03-15-1990 should parse correctly',
      category: 'Date Format Parsing',
      iin: 'CA',
      data: {
        DCS: 'DASHFORMAT',
        DAC: 'TEST',
        DBB: '03-15-1990', // With dashes
        DBA: '03-15-2030',
        DAG: '100 Dash Road',
        DAI: 'San Diego',
        DAJ: 'CA',
        DAK: '92101',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Mar 15, 1990 - Dashes stripped, parses correctly',
      },
    },
    {
      id: 'test-17',
      title: 'Test 17: Dates with Separators',
      description: '03/15/1990 should strip to 03151990 and parse',
      category: 'Date Format Parsing',
      iin: 'CA',
      data: {
        DCS: 'SEPARATOR',
        DAC: 'TEST',
        DBB: '03/15/1990', // With separators
        DBA: '03/15/2030',
        DAG: '100 Slash Avenue',
        DAI: 'Sacramento',
        DAJ: 'CA',
        DAK: '95814',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Mar 15, 1990 - Separators stripped, parses correctly',
      },
    },
    {
      id: 'test-18',
      title: 'Test 18: Leap Year Feb 29',
      description: 'Feb 29, 2000 birth date - valid leap year',
      category: 'Date Format Parsing',
      iin: 'CA',
      data: {
        DCS: 'LEAPYEAR',
        DAC: 'BIRTHDAY',
        DBB: '02292000', // Feb 29, 2000 (leap year)
        DBA: getDateOffset(365 * 5),
        DAG: '229 February Street',
        DAI: 'Coquitlam',
        DAJ: 'BC',
        DAK: 'V3K 4F6',
        DAY: 'HAZ',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'DOB: Feb 29, 2000 - Leap year date parses correctly',
      },
    },

    // ================================================================
    // CATEGORY 5: AGE VERIFICATION EDGE CASES (Tests 19-24)
    // Purpose: Test exact age boundary conditions
    // ================================================================
    {
      id: 'test-19',
      title: 'Test 19: Exactly 21 Today',
      description: 'Birthday is exactly 21 years ago today',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'TWENTYONE',
        DAC: 'TODAY',
        DBB: getExactBirthday(21), // Exactly 21 years ago
        DBA: getDateOffset(365 * 5),
        DAG: '21 Legal Avenue',
        DAI: 'Las Vegas',
        DAJ: 'NV',
        DAK: '89101',
        DAY: 'BLU',
        DAU: '509',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED - Age 21',
        notes: 'Exactly 21 today - should be VERIFIED for 21+ venues',
      },
    },
    {
      id: 'test-20',
      title: 'Test 20: Turns 21 Tomorrow',
      description: 'Birthday 21 years ago + 1 day - currently 20',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'ALMOSTTHERE',
        DAC: 'TOMORROW',
        DBB: getBirthdayTurnsTomorrow(21), // Turns 21 tomorrow
        DBA: getDateOffset(365 * 5),
        DAG: '20 Almost Lane',
        DAI: 'Las Vegas',
        DAJ: 'NV',
        DAK: '89101',
        DAY: 'GRN',
        DAU: '507',
        DBC: '2',
      },
      expected: {
        type: 'invalid',
        status: 'UNDERAGE - Age 20',
        notes: 'Still 20 until tomorrow - should show UNDERAGE for 21+ default',
      },
    },
    {
      id: 'test-21',
      title: 'Test 21: 18 Years Old',
      description: 'Legal adult but under 21',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'EIGHTEEN',
        DAC: 'ADULT',
        DBB: getExactBirthday(18),
        DBA: getDateOffset(365 * 5),
        DAG: '18 Adult Street',
        DAI: 'Phoenix',
        DAJ: 'AZ',
        DAK: '85001',
        DAY: 'BRO',
        DAU: '506',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: 'UNDERAGE - Age 18',
        notes: 'Age 18 - UNDERAGE for default 21+ setting (valid for 18+ venues)',
      },
    },
    {
      id: 'test-22',
      title: 'Test 22: 65 Years Old Senior',
      description: 'Older person - tests larger age calculation',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'SENIOR',
        DAC: 'CITIZEN',
        DBB: getExactBirthday(65),
        DBA: getDateOffset(365 * 2),
        DAG: '65 Elder Lane',
        DAI: 'Scottsdale',
        DAJ: 'AZ',
        DAK: '85251',
        DAY: 'GRY',
        DAU: '504',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED - Age 65',
        notes: 'Age 65 - VERIFIED with correct age calculation',
      },
    },
    {
      id: 'test-23',
      title: 'Test 23: Exactly 120 Years Old',
      description: 'At max valid age threshold - should NOT flag',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'SUPERCENTENARIAN',
        DAC: 'OLDEST',
        DBB: getExactBirthday(120),
        DBA: getDateOffset(365),
        DAG: '120 Long Life Road',
        DAI: 'Okinawa',
        DAJ: 'CA',
        DAK: '90000',
        DAY: 'GRY',
        DAU: '500',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Age 120 - At threshold, should NOT trigger age warning',
      },
    },
    {
      id: 'test-24',
      title: 'Test 24: Exactly 121 Years Old',
      description: 'Just over max age - SHOULD flag',
      category: 'Age Verification Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'TOOOLD',
        DAC: 'IMPOSSIBLE',
        DBB: getExactBirthday(121),
        DBA: getDateOffset(365),
        DAG: '121 Impossible Street',
        DAI: 'Nowhere',
        DAJ: 'CA',
        DAK: '90000',
        DAY: 'GRY',
        DAU: '500',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: '75% - Review Recommended',
        notes: 'Age 121 - CRITICAL flag: age over 120 threshold',
      },
    },

    // ================================================================
    // CATEGORY 6: EXPIRATION DATE EDGE CASES (Tests 25-28)
    // Purpose: Test expiration boundary conditions
    // ================================================================
    {
      id: 'test-25',
      title: 'Test 25: Expires Today',
      description: 'Card expiring today - should be valid all day',
      category: 'Expiration Date Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'EXPIRING',
        DAC: 'TODAY',
        DBB: '05151990',
        DBA: getTodayMMDDCCYY(), // Expires today
        DAG: '123 Last Day Street',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
        DAY: 'BRO',
        DAU: '508',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: `Expires: ${formatDate(getTodayMMDDCCYY())} - Should be VALID all day today`,
      },
    },
    {
      id: 'test-26',
      title: 'Test 26: Expires Tomorrow',
      description: 'Clearly valid - one day remaining',
      category: 'Expiration Date Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'EXPIRING',
        DAC: 'TOMORROW',
        DBB: '06151985',
        DBA: getDateOffset(1), // Expires tomorrow
        DAG: '456 Future Ave',
        DAI: 'San Francisco',
        DAJ: 'CA',
        DAK: '94102',
        DAY: 'BLU',
        DAU: '511',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: `Expires: ${formatDate(getDateOffset(1))} - Should be VALID`,
      },
    },
    {
      id: 'test-27',
      title: 'Test 27: Expired Yesterday',
      description: 'Just expired - should show EXPIRED',
      category: 'Expiration Date Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'EXPIRED',
        DAC: 'YESTERDAY',
        DBB: '03201988',
        DBA: getDateOffset(-1), // Expired yesterday
        DAG: '789 Past Lane',
        DAI: 'Sacramento',
        DAJ: 'CA',
        DAK: '95814',
        DAY: 'GRN',
        DAU: '602',
        DBC: '1',
      },
      expected: {
        type: 'invalid',
        status: 'EXPIRED ID',
        notes: `Expired: ${formatDate(getDateOffset(-1))} - Should show EXPIRED`,
      },
    },
    {
      id: 'test-28',
      title: 'Test 28: Expired 5 Years Ago',
      description: 'Long expired ID',
      category: 'Expiration Date Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'LONGEXPIRED',
        DAC: 'ANCIENT',
        DBB: '07041980',
        DBA: getDateOffset(-365 * 5), // Expired 5 years ago
        DAG: '111 Forgotten Street',
        DAI: 'Bakersfield',
        DAJ: 'CA',
        DAK: '93301',
        DAY: 'HAZ',
        DAU: '507',
        DBC: '2',
      },
      expected: {
        type: 'invalid',
        status: 'EXPIRED ID',
        notes: 'Expired 5 years ago - Should show EXPIRED',
      },
    },

    // ================================================================
    // CATEGORY 7: HEIGHT FORMAT VARIATIONS (Tests 29-35)
    // Purpose: Test all height parsing formats
    // ================================================================
    {
      id: 'test-29',
      title: 'Test 29: 3-Digit Format Short (501)',
      description: '5 feet 1 inch',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'SHORTPERSON',
        DAC: 'HEIGHT',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Short Street',
        DAI: 'San Jose',
        DAJ: 'CA',
        DAK: '95101',
        DAU: '501', // 5 feet 1 inch
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 501 → Displays as 5\'01"',
      },
    },
    {
      id: 'test-30',
      title: 'Test 30: 3-Digit Format Tall (611)',
      description: '6 feet 11 inches',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'TALLPERSON',
        DAC: 'HEIGHT',
        DBB: '03201988',
        DBA: getDateOffset(365 * 5),
        DAG: '789 Tall Avenue',
        DAI: 'Oakland',
        DAJ: 'CA',
        DAK: '94601',
        DAU: '611', // 6 feet 11 inches
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 611 → Displays as 6\'11"',
      },
    },
    {
      id: 'test-31',
      title: 'Test 31: Total Inches Low (60)',
      description: '60 inches = 5 feet 0 inches',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'INCHESLOW',
        DAC: 'FORMAT',
        DBB: '07151992',
        DBA: getDateOffset(365 * 5),
        DAG: '456 Inch Road',
        DAI: 'Fresno',
        DAJ: 'CA',
        DAK: '93701',
        DAU: '60', // 60 total inches
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 60 → Displays as 5\'00" (60 inches = 5 feet)',
      },
    },
    {
      id: 'test-32',
      title: 'Test 32: Total Inches Mid (69)',
      description: '69 inches = 5 feet 9 inches',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'INCHESMID',
        DAC: 'FORMAT',
        DBB: '09101985',
        DBA: getDateOffset(365 * 5),
        DAG: '789 Measure Lane',
        DAI: 'Anaheim',
        DAJ: 'CA',
        DAK: '92801',
        DAU: '69', // 69 total inches
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 69 → Displays as 5\'09" (69 inches)',
      },
    },
    {
      id: 'test-33',
      title: 'Test 33: Total Inches High (80)',
      description: '80 inches = 6 feet 8 inches',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'INCHESHIGH',
        DAC: 'FORMAT',
        DBB: '11221987',
        DBA: getDateOffset(365 * 5),
        DAG: '321 Basketball Court',
        DAI: 'Long Beach',
        DAJ: 'CA',
        DAK: '90801',
        DAU: '80', // 80 total inches
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 80 → Displays as 6\'08" (80 inches)',
      },
    },
    {
      id: 'test-34',
      title: 'Test 34: Centimeters (175)',
      description: 'Metric format - 175 cm',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'METRIC',
        DAC: 'HEIGHT',
        DBB: '06301993',
        DBA: getDateOffset(365 * 5),
        DAG: '100 Metric Boulevard',
        DAI: 'Santa Monica',
        DAJ: 'CA',
        DAK: '90401',
        DAU: '175', // 175 cm
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 175 → Displays as "175" (centimeters)',
      },
    },
    {
      id: 'test-35',
      title: 'Test 35: Edge Case (100)',
      description: 'Boundary value between formats',
      category: 'Height Format Variations',
      iin: 'CA',
      data: {
        DCS: 'EDGECASE',
        DAC: 'HEIGHT',
        DBB: '04151989',
        DBA: getDateOffset(365 * 5),
        DAG: '999 Boundary Street',
        DAI: 'Pasadena',
        DAJ: 'CA',
        DAK: '91101',
        DAU: '100', // Boundary value
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Height: 100 → Displays as "100" (treated as centimeters at boundary)',
      },
    },

    // ================================================================
    // CATEGORY 8: EYE & HAIR COLOR CODES (Tests 36-41)
    // Purpose: Verify all color code variations are accepted
    // ================================================================
    {
      id: 'test-36',
      title: 'Test 36: Standard AAMVA Eye (BRO)',
      description: 'Standard brown eye code',
      category: 'Eye & Hair Color Codes',
      iin: 'CA',
      data: {
        DCS: 'BROWNEYES',
        DAC: 'STANDARD',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Color Street',
        DAI: 'Irvine',
        DAJ: 'CA',
        DAK: '92602',
        DAY: 'BRO', // Standard brown
        DAU: '508',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Eye color BRO parsed correctly - no warning',
      },
    },
    {
      id: 'test-37',
      title: 'Test 37: Hawaii Variant (BRN)',
      description: 'Hawaii uses BRN instead of BRO for brown',
      category: 'Eye & Hair Color Codes',
      iin: 'HI',
      data: {
        DCS: 'HAWAIIBROWN',
        DAC: 'VARIANT',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '100 Aloha Drive',
        DAI: 'Honolulu',
        DAJ: 'HI',
        DAK: '96815',
        DAY: 'BRN', // Hawaii variant for brown
        DAU: '507',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Eye color BRN (Hawaii variant) accepted - no false positive',
      },
    },
    {
      id: 'test-38',
      title: 'Test 38: Idaho Variant (BWN)',
      description: 'Idaho uses BWN for brown',
      category: 'Eye & Hair Color Codes',
      iin: 'CA',
      data: {
        DCS: 'IDAHOBROWN',
        DAC: 'VARIANT',
        DBB: '08201985',
        DBA: getDateOffset(365 * 5),
        DAG: '200 Potato Lane',
        DAI: 'Boise',
        DAJ: 'ID',
        DAK: '83701',
        DAY: 'BWN', // Idaho variant for brown
        DAU: '510',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Eye color BWN (Idaho variant) accepted - no false positive',
      },
    },
    {
      id: 'test-39',
      title: 'Test 39: Full Word Eye Color (BROWN)',
      description: 'Some states use full words instead of codes',
      category: 'Eye & Hair Color Codes',
      iin: 'CA',
      data: {
        DCS: 'FULLWORD',
        DAC: 'EYECOLOR',
        DBB: '03101992',
        DBA: getDateOffset(365 * 5),
        DAG: '300 Verbose Street',
        DAI: 'Denver',
        DAJ: 'CO',
        DAK: '80201',
        DAY: 'BROWN', // Full word
        DAU: '506',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Eye color BROWN (full word) accepted - no false positive',
      },
    },
    {
      id: 'test-40',
      title: 'Test 40: All Standard Hair Codes',
      description: 'Tests BLK hair color',
      category: 'Eye & Hair Color Codes',
      iin: 'CA',
      data: {
        DCS: 'HAIRTEST',
        DAC: 'ALLCODES',
        DBB: '07221989',
        DBA: getDateOffset(365 * 5),
        DAG: '400 Hair Lane',
        DAI: 'Portland',
        DAJ: 'OR',
        DAK: '97201',
        DAY: 'BLU',
        DAZ: 'BLK', // Black hair
        DAU: '509',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Hair color BLK parsed correctly - no warning',
      },
    },
    {
      id: 'test-41',
      title: 'Test 41: Unusual Color (DIC)',
      description: 'Dichromatic eyes - rare but valid code',
      category: 'Eye & Hair Color Codes',
      iin: 'CA',
      data: {
        DCS: 'DICHROMATIC',
        DAC: 'EYES',
        DBB: '11151991',
        DBA: getDateOffset(365 * 5),
        DAG: '500 Unique Street',
        DAI: 'Seattle',
        DAJ: 'WA',
        DAK: '98101',
        DAY: 'DIC', // Dichromatic
        DAU: '511',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Eye color DIC (dichromatic) accepted - rare but valid',
      },
    },

    // ================================================================
    // CATEGORY 9: GENDER CODE VARIATIONS (Tests 42-45)
    // Purpose: Test all gender encoding formats
    // ================================================================
    {
      id: 'test-42',
      title: 'Test 42: Gender Code 1 (Male)',
      description: 'Numeric code 1 = Male',
      category: 'Gender Code Variations',
      iin: 'CA',
      data: {
        DCS: 'GENDERMALE',
        DAC: 'NUMERIC',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Gender Street',
        DAI: 'Chicago',
        DAJ: 'IL',
        DAK: '60601',
        DBC: '1', // Male
        DAU: '510',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Gender: 1 → Displays as "Male"',
      },
    },
    {
      id: 'test-43',
      title: 'Test 43: Gender Code 2 (Female)',
      description: 'Numeric code 2 = Female',
      category: 'Gender Code Variations',
      iin: 'CA',
      data: {
        DCS: 'GENDERFEMALE',
        DAC: 'NUMERIC',
        DBB: '06201988',
        DBA: getDateOffset(365 * 5),
        DAG: '456 Gender Avenue',
        DAI: 'Miami',
        DAJ: 'FL',
        DAK: '33101',
        DBC: '2', // Female
        DAU: '505',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Gender: 2 → Displays as "Female"',
      },
    },
    {
      id: 'test-44',
      title: 'Test 44: Gender Code M/F (Letter)',
      description: 'Letter codes M = Male',
      category: 'Gender Code Variations',
      iin: 'CA',
      data: {
        DCS: 'GENDERLETTER',
        DAC: 'FORMAT',
        DBB: '09101985',
        DBA: getDateOffset(365 * 5),
        DAG: '789 Alpha Lane',
        DAI: 'Boston',
        DAJ: 'MA',
        DAK: '02101',
        DBC: 'M', // Letter M
        DAU: '508',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Gender: M → Displays as "Male"',
      },
    },
    {
      id: 'test-45',
      title: 'Test 45: Gender Code 9 (Unknown)',
      description: 'Code 9 = Unknown/Unspecified',
      category: 'Gender Code Variations',
      iin: 'CA',
      data: {
        DCS: 'GENDERUNKNOWN',
        DAC: 'UNSPECIFIED',
        DBB: '12051993',
        DBA: getDateOffset(365 * 5),
        DAG: '321 Neutral Boulevard',
        DAI: 'Atlanta',
        DAJ: 'GA',
        DAK: '30301',
        DBC: '9', // Unknown
        DAU: '507',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Gender: 9 → Displays as-is or "Unknown"',
      },
    },

    // ================================================================
    // CATEGORY 10: STATE-SPECIFIC VARIATIONS (Tests 46-49)
    // Purpose: Test state-specific field handling
    // ================================================================
    {
      id: 'test-46',
      title: 'Test 46: Washington State (DCT)',
      description: 'Washington uses DCT instead of DAC for first name',
      category: 'State-Specific Variations',
      iin: 'WA',
      data: {
        DCS: 'WASHINGTON',
        DCT: 'FIRSTNAME', // Washington uses DCT not DAC
        DAD: 'MIDDLE',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '100 Evergreen Way',
        DAI: 'Seattle',
        DAJ: 'WA',
        DAK: '98101',
        DAY: 'BLU',
        DAU: '509',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'First name from DCT (Washington variant) parsed correctly',
      },
    },
    {
      id: 'test-47',
      title: 'Test 47: Canadian BC (YYYYMMDD)',
      description: 'British Columbia uses YYYYMMDD date format',
      category: 'State-Specific Variations',
      iin: 'BC',
      data: {
        DCS: 'VANCOUVER',
        DAC: 'CANADIAN',
        DAD: 'PERSON',
        DBB: getYearsAgoYYYYMMDD(30), // YYYYMMDD
        DBA: getDateOffsetYYYYMMDD(365 * 5),
        DBD: getDateOffsetYYYYMMDD(-365 * 2),
        DAG: '123 BC Street',
        DAI: 'Vancouver',
        DAJ: 'BC',
        DAK: 'V6B 2K2',
        DAY: 'BRO',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'All YYYYMMDD dates parsed correctly for BC',
      },
    },
    {
      id: 'test-48',
      title: 'Test 48: Florida K-Suffix CDL',
      description: 'Commercial DL variation',
      category: 'State-Specific Variations',
      iin: 'FL',
      data: {
        DCS: 'FLORIDA',
        DAC: 'DRIVER',
        DAD: 'COMMERCIAL',
        DBB: '08151985',
        DBA: getDateOffset(365 * 4),
        DAG: '456 Sunshine Boulevard',
        DAI: 'Miami',
        DAJ: 'FL',
        DAK: '33101',
        DAY: 'BRO',
        DAU: '602',
        DBC: '1',
        DAQ: 'F123456789012K', // K-suffix CDL
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Florida CDL with K-suffix parsed correctly',
      },
    },
    {
      id: 'test-49',
      title: 'Test 49: Multiple IIN States',
      description: 'Tests different IIN code recognition',
      category: 'State-Specific Variations',
      iin: 'TX',
      data: {
        DCS: 'TEXAN',
        DAC: 'LONE',
        DAD: 'STAR',
        DBB: '07041980',
        DBA: getDateOffset(365 * 6),
        DAG: '789 Lone Star Drive',
        DAI: 'Houston',
        DAJ: 'TX',
        DAK: '77001',
        DAY: 'GRN',
        DAU: '511',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Texas IIN (636015) recognized and parsed correctly',
      },
    },

    // ================================================================
    // CATEGORY 11: NAME PARSING EDGE CASES (Tests 50-54)
    // Purpose: Test special characters and formats in names
    // ================================================================
    {
      id: 'test-50',
      title: 'Test 50: Hyphenated Last Name',
      description: 'Last name with hyphen',
      category: 'Name Parsing Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'JEAN-PIERRE',
        DAC: 'MARIE',
        DAD: 'CLAUDE',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Hyphen Street',
        DAI: 'Montreal',
        DAJ: 'QC',
        DAK: 'H2X 1Y4',
        DAU: '508',
        DBC: '2',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Hyphenated name "JEAN-PIERRE" preserved correctly',
      },
    },
    {
      id: 'test-51',
      title: 'Test 51: Apostrophe in Name',
      description: "Name with apostrophe - O'BRIEN",
      category: 'Name Parsing Edge Cases',
      iin: 'CA',
      data: {
        DCS: "O'BRIEN",
        DAC: 'PATRICK',
        DAD: 'SEAN',
        DBB: '03171988',
        DBA: getDateOffset(365 * 5),
        DAG: '456 Irish Lane',
        DAI: 'Boston',
        DAJ: 'MA',
        DAK: '02101',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: "Apostrophe in name \"O'BRIEN\" preserved correctly",
      },
    },
    {
      id: 'test-52',
      title: 'Test 52: Very Long Names',
      description: '40+ character name',
      category: 'Name Parsing Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'WOLFESCHLEGELSTEINHAUSENBERGERDORFF',
        DAC: 'BARTHOLOMEW',
        DAD: 'MONTGOMERY',
        DBB: '09221985',
        DBA: getDateOffset(365 * 5),
        DAG: '789 Long Name Boulevard',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
        DAU: '507',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Very long last name (35+ chars) preserved without truncation',
      },
    },
    {
      id: 'test-53',
      title: 'Test 53: Single Name Only',
      description: 'Only last name present (no first name)',
      category: 'Name Parsing Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'MONONYM',
        // No DAC (first name)
        DBB: '01011990',
        DBA: getDateOffset(365 * 5),
        DAG: '111 Single Street',
        DAI: 'San Diego',
        DAJ: 'CA',
        DAK: '92101',
        DAU: '506',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '90% Confidence',
        notes: 'Single name handled gracefully (warning but functional)',
      },
    },
    {
      id: 'test-54',
      title: 'Test 54: Name with Diacritics',
      description: 'Name with accented characters',
      category: 'Name Parsing Edge Cases',
      iin: 'CA',
      data: {
        DCS: 'GARCIA',
        DAC: 'JOSE',
        DAD: 'MARIA',
        DBB: '12121992',
        DBA: getDateOffset(365 * 5),
        DAG: '222 Accent Avenue',
        DAI: 'San Antonio',
        DAJ: 'TX',
        DAK: '78201',
        DAU: '509',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Name "GARCIA" preserved (diacritics may be stripped in barcode)',
      },
    },

    // ================================================================
    // CATEGORY 12: FIELD PRESENCE VARIATIONS (Tests 55-58)
    // Purpose: Verify optional fields don't cause false positives
    // ================================================================
    {
      id: 'test-55',
      title: 'Test 55: Missing DCF (Document Discriminator)',
      description: 'No document discriminator field',
      category: 'Field Presence Variations',
      iin: 'CA',
      data: {
        DCS: 'NODCF',
        DAC: 'MISSING',
        DAD: 'FIELD',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Optional Street',
        DAI: 'Sacramento',
        DAJ: 'CA',
        DAK: '95814',
        DAY: 'BRO',
        DAU: '508',
        DBC: '1',
        // No DCF
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'No DCF field - should NOT flag (optional per AAMVA)',
      },
    },
    {
      id: 'test-56',
      title: 'Test 56: Missing Physical Description',
      description: 'No height, weight, or color fields',
      category: 'Field Presence Variations',
      iin: 'CA',
      data: {
        DCS: 'NOPHYSICAL',
        DAC: 'DESCRIPTION',
        DBB: '08201988',
        DBA: getDateOffset(365 * 5),
        DAG: '456 Simple Lane',
        DAI: 'Fresno',
        DAJ: 'CA',
        DAK: '93701',
        DBC: '1',
        // No DAU, DAY, DAZ
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Missing physical description - should NOT flag',
      },
    },
    {
      id: 'test-57',
      title: 'Test 57: Missing Address',
      description: 'No address fields present',
      category: 'Field Presence Variations',
      iin: 'CA',
      data: {
        DCS: 'NOADDRESS',
        DAC: 'HOMELESS',
        DBB: '03101985',
        DBA: getDateOffset(365 * 5),
        // No DAG, DAI, DAJ, DAK
        DAY: 'BLU',
        DAU: '510',
        DBC: '1',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'Missing address fields - should NOT flag',
      },
    },
    {
      id: 'test-58',
      title: 'Test 58: All Optional Fields Present',
      description: 'Every possible field populated',
      category: 'Field Presence Variations',
      iin: 'CA',
      data: {
        DCS: 'COMPLETE',
        DAC: 'PERFECT',
        DAD: 'EXAMPLE',
        DBB: '05151990',
        DBA: getDateOffset(365 * 5),
        DBD: getDateOffset(-365 * 2),
        DAG: '456 Oak Avenue',
        DAI: 'San Francisco',
        DAJ: 'CA',
        DAK: '94102',
        DAY: 'BLU',
        DAZ: 'BRO',
        DAU: '602',
        DBC: '2',
        DCF: 'ABC1234567890',
        DCG: 'USA',
        DDE: 'N',
        DDF: 'N',
        DDG: 'N',
        DAQ: 'D1234567',
      },
      expected: {
        type: 'valid',
        status: '100% Confidence',
        notes: 'All optional fields present - perfect baseline',
      },
    },

    // ================================================================
    // CATEGORY 13: PASSBACK DETECTION (Tests 59-61)
    // Purpose: Test duplicate ID detection (manual test with same barcode)
    // ================================================================
    {
      id: 'test-59',
      title: 'Test 59: Passback Test ID A',
      description: 'Scan twice within 4 hours to test passback detection',
      category: 'Passback Detection',
      iin: 'CA',
      data: {
        DCS: 'PASSBACK',
        DAC: 'TESTA',
        DAD: 'DUPLICATE',
        DBB: '05151995',
        DBA: getDateOffset(365 * 5),
        DAG: '123 Passback Street',
        DAI: 'Los Angeles',
        DAJ: 'CA',
        DAK: '90001',
        DAY: 'BRO',
        DAU: '508',
        DBC: '1',
        DAQ: 'PASSBACK001', // Unique ID for tracking
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED (1st scan) / PASSBACK ALERT (2nd scan)',
        notes: 'Scan twice within 4 hours - second scan should show alert',
      },
    },
    {
      id: 'test-60',
      title: 'Test 60: Passback Test ID B',
      description: 'Different ID for comparison - should never alert',
      category: 'Passback Detection',
      iin: 'CA',
      data: {
        DCS: 'PASSBACK',
        DAC: 'TESTB',
        DAD: 'DIFFERENT',
        DBB: '06201993',
        DBA: getDateOffset(365 * 5),
        DAG: '456 Unique Avenue',
        DAI: 'San Diego',
        DAJ: 'CA',
        DAK: '92101',
        DAY: 'BLU',
        DAU: '505',
        DBC: '2',
        DAQ: 'PASSBACK002',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'Different ID - should never trigger passback alert',
      },
    },
    {
      id: 'test-61',
      title: 'Test 61: Passback Test ID C',
      description: 'Test passback after 4+ hour window expires',
      category: 'Passback Detection',
      iin: 'CA',
      data: {
        DCS: 'PASSBACK',
        DAC: 'TESTC',
        DAD: 'WINDOWTEST',
        DBB: '09101990',
        DBA: getDateOffset(365 * 5),
        DAG: '789 Window Lane',
        DAI: 'Oakland',
        DAJ: 'CA',
        DAK: '94601',
        DAY: 'GRN',
        DAU: '510',
        DBC: '1',
        DAQ: 'PASSBACK003',
      },
      expected: {
        type: 'valid',
        status: 'VERIFIED',
        notes: 'After 4+ hours, should NOT trigger passback alert on rescan',
      },
    },

    // ================================================================
    // CATEGORY 14: MALFORMED DATA HANDLING (Tests 62-65)
    // Purpose: Verify graceful handling of bad data
    // ================================================================
    {
      id: 'test-62',
      title: 'Test 62: Missing Expiration Date',
      description: 'Has DOB but no expiration - tests missing DBA handling',
      category: 'Malformed Data Handling',
      iin: 'CA',
      data: {
        DCS: 'NOEXPIRY',
        DAC: 'TEST',
        DBB: '01011990',
        // No DBA (expiration)
      },
      expected: {
        type: 'invalid',
        status: 'EXPIRED ID',
        notes: 'Missing expiration date treated as expired (correct behavior)',
      },
    },
    {
      id: 'test-63',
      title: 'Test 63: Missing Date Fields',
      description: 'No DBB (DOB) or DBA (expiration)',
      category: 'Malformed Data Handling',
      iin: 'CA',
      data: {
        DCS: 'NODATES',
        DAC: 'MISSING',
        DAD: 'DATES',
        DAG: '123 Dateless Street',
        DAI: 'Timeless',
        DAJ: 'CA',
        DAK: '00000',
        // No DBB or DBA
      },
      expected: {
        type: 'invalid',
        status: 'Warning - Missing Dates',
        notes: 'No date fields - graceful handling with warning',
      },
    },
    {
      id: 'test-64',
      title: 'Test 64: Garbled Field Data',
      description: 'Invalid date strings',
      category: 'Malformed Data Handling',
      iin: 'CA',
      data: {
        DCS: 'GARBLED',
        DAC: 'DATA',
        DBB: 'NOTADATE', // Invalid
        DBA: '99999999', // Invalid
        DAG: '123 Corrupt Street',
        DAI: 'Broken',
        DAJ: 'CA',
        DAK: '00000',
      },
      expected: {
        type: 'invalid',
        status: 'Warning - Invalid Data',
        notes: 'Garbled dates - graceful failure with warning',
      },
    },
    {
      id: 'test-65',
      title: 'Test 65: Empty Fields',
      description: 'Field codes with no values',
      category: 'Malformed Data Handling',
      iin: 'CA',
      data: {
        DCS: '',
        DAC: '',
        DBB: '',
        DBA: '',
        DAG: '',
        DAI: '',
        DAJ: '',
        DAK: '',
      },
      expected: {
        type: 'invalid',
        status: 'Warning - Empty Fields',
        notes: 'All empty fields - handles gracefully without crash',
      },
    },
  ];
}
